/**
 * Where a card's background picture is fetched from.
 *
 * The picture is a blob in the author's own repo — that is the only thing a
 * styling decision can travel as — but the app has no way to build a URL for a
 * blob itself: every picture it draws arrives already addressed, because the
 * service in front of the repos (`packages/bsky/src/index.ts`) is the side that
 * knows the image host and the transformations it offers. A mark written by an
 * unknown client cannot ask it to resolve anything.
 *
 * So the host is *borrowed from a URL the app already has*: the post's own
 * author is drawn with an avatar, the avatar came back addressed by that same
 * service, and the addressing scheme is the one thing we need — the rest of the
 * path is replaced with the blob's own id and the preset a card wants (a picture
 * as large as the service will give, rather than a timeline thumbnail). That
 * keeps the feature working on any deployment, including one whose image host is
 * not `cdn.bsky.app`, and it costs nothing: no new endpoint, no lookup.
 *
 * Two things can be the picture, and the mark says which:
 *
 *  - **the post's own picture** (a mark with no blob in it), which is what the
 *    design's own controls put there — the author adds a picture to the post, and
 *    the design is drawn over it. There is nothing to address: the URL is the one
 *    the post's own embed already has, handed in by the caller;
 *  - **a named blob** (a mark written by the first version of this feature, or by
 *    one that keeps a picture the post no longer carries), addressed the way above.
 *
 * When neither is at hand the answer is `undefined`, and the card draws the colour
 * it kept beside the picture. A background that cannot be fetched is a plainer card,
 * never a broken one.
 */
import {type StyledPost} from '#/state/postStyle/types'

/**
 * A picture URL as the service writes it: a preset, then `plain/{repo}/{blob}`.
 *
 * Anchored at both ends so a URL of any other shape — a video thumbnail, a feed's
 * own banner, something a different service wrote — is refused rather than
 * patched into a plausible-looking address that would 404.
 */
const IMAGE_URL_RE =
  /^(.*\/img\/)[a-z_]+\/(?:plain\/)?[^/]+\/[^/@]+@[a-z0-9]+$/i

/** The format to ask for, from the type the author uploaded. */
function formatForMimeType(mimeType: string | undefined): string {
  if (mimeType === 'image/png') return 'png'
  if (mimeType === 'image/gif') return 'gif'
  return 'jpeg'
}

/**
 * The URL for a card's background picture.
 *
 * `postImageUrl` is the post's *own* picture, for a mark that names no blob: the
 * design is drawn over the picture the post already carries, and this app is already
 * holding its address (the feed's own embed). `avatarUrl` is the addressing hint the
 * named-blob case borrows — in practice the post author's avatar. Either may be
 * missing, and then the card draws the colour it kept beside the picture.
 */
export function styledBackgroundImageUrl(
  style: Pick<StyledPost, 'background'>,
  {avatarUrl, postImageUrl}: {avatarUrl?: string; postImageUrl?: string} = {},
): string | undefined {
  const background = style.background
  if (background.type !== 'image') return undefined

  /*
   * The post's own picture: nothing to build. It is already addressed by whatever
   * gave this app the post, and it cannot point anywhere else.
   */
  if (!background.did || !background.cid) return postImageUrl

  const match = avatarUrl ? IMAGE_URL_RE.exec(avatarUrl) : null
  /* A blob we cannot address is still a card: the post's picture if there is one,
     and the colour beside it if there is not. */
  if (!match) return postImageUrl
  const format = formatForMimeType(background.mimeType)
  return `${match[1]}feed_fullsize/plain/${background.did}/${background.cid}@${format}`
}
